import { useEffect, useRef, useState } from "react";

/**
 * iter87.199.146 — Shared "more content below" scroll cue for the legal
 * modals (TermsModal, PrivacyModal, ProPlusBillingTermsModal). Returns a
 * ref to attach to the scrollable container plus a boolean telling the
 * caller whether to render the fade/arrow hint (only true when the
 * content overflows AND the user hasn't scrolled near the bottom yet).
 *
 * `deps` should include anything that changes the rendered content
 * height after mount (e.g. `open`, async-loaded markdown) so the
 * overflow check re-runs once the real content is in the DOM.
 */
export function useScrollCue(deps = []) {
    const ref = useRef(null);
    const [showCue, setShowCue] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) { setShowCue(false); return; }
        const check = () => {
            const overflowing = el.scrollHeight > el.clientHeight + 4;
            const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
            setShowCue(overflowing && !nearBottom);
        };
        check();
        el.addEventListener("scroll", check, { passive: true });
        const ro = new ResizeObserver(check);
        ro.observe(el);
        return () => {
            el.removeEventListener("scroll", check);
            ro.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);

    return [ref, showCue];
}
